import { TId } from '@apia/util';
import { ReactNode, useContext, useEffect, useMemo, useState } from 'react';
import { AccordionContext, AccordionItemContext } from './context';
import { TItemState, TItemStateListener, TPropsListener } from './types';
import { shallowEqual } from '@apia/store';
import { IAccordionProps } from './Accordion';

type TSelectionComparator<T> = (prev: T, next: T) => boolean;

type TItemUpdater =
  | Partial<TItemState>
  | ((currentProps: TItemState) => Partial<TItemState>);

function makeItemStateSelectorHook(handler: AccordionHandler) {
  return function useItemStateSelector<T>(
    selector: (props: TItemState) => T,
    comparator: TSelectionComparator<T> = shallowEqual,
  ) {
    const id = handler.hooks.useItemId();
    const [state, setState] = useState<T>(selector(handler.itemsState[id]));

    handler.hooks.useItemStateUpdate((props) => {
      const newState = selector(props);
      if (!comparator(state, newState)) {
        setState(newState);
      }
    });

    return state;
  };
}

function makePropsSelectorHook(handler: AccordionHandler) {
  return function usePropsSelector<T>(
    selector: (props: IAccordionProps) => T,
    comparator: TSelectionComparator<T> = shallowEqual,
  ) {
    const [state, setState] = useState<T>(selector(handler.props));

    handler.hooks.usePropsUpdate((props) => {
      const newState = selector(props);
      if (!comparator(state, newState)) {
        setState(newState);
      }
    });

    return state;
  };
}

export class AccordionHandler {
  itemsState: Record<TId, TItemState> = {};
  itemsStateListeners: Record<TId, TItemStateListener[]> = {};

  propsListeners: TPropsListener[] = [];

  constructor(
    public id: string,
    public props: IAccordionProps,
  ) {}

  registerItem(itemId: TId, initialState: TItemState) {
    if (!this.itemsState[itemId]) this.itemsState[itemId] = initialState;
  }

  toggleItem(itemId: TId, expanded?: boolean) {
    const isExpanded = expanded ?? !this.itemsState[itemId].isExpanded;
    if (this.props.singleExpand && isExpanded) {
      Object.keys(this.itemsState).forEach((current) => {
        if (current !== itemId) {
          this.udpateItem(current, { isExpanded: false });
        }
      });
    }
    this.udpateItem(itemId, { isExpanded });
  }

  unregisterItem(itemId: TId) {
    // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
    delete this.itemsState[itemId];
    // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
    delete this.itemsStateListeners[itemId];
  }

  /**
   * Permite actualizar parcialmente el estado de un item
   */
  udpateItem(itemId: TId, updater: TItemUpdater) {
    const newProps =
      updater instanceof Function ? updater(this.itemsState[itemId]) : updater;

    Object.assign(this.itemsState[itemId], newProps);
    this.itemsStateListeners[itemId].forEach((current) =>
      current(this.itemsState[itemId]),
    );
  }

  updateProps(props: IAccordionProps) {
    this.props = { ...this.props, ...props };
    this.propsListeners.forEach((current) => current(props));
  }

  hooks = {
    useAccordionContextProvider: () => {
      const Provider = useMemo(
        () =>
          ({ children }: { children: ReactNode }) => (
            <AccordionContext.Provider value={this}>
              {children}
            </AccordionContext.Provider>
          ),
        [],
      );
      return Provider;
    },
    useItemContextProvider: (itemId: TId) => {
      const Provider = useMemo(
        () =>
          ({ children }: { children: ReactNode }) => (
            <AccordionItemContext.Provider value={itemId}>
              {children}
            </AccordionItemContext.Provider>
          ),
        [itemId],
      );
      return Provider;
    },
    useItemId: () => {
      const id = useContext(AccordionItemContext);
      if (id === null) throw new Error('There is no AccordionItemContext');
      return id;
    },
    useItemStateSelector: makeItemStateSelectorHook(this),
    useItemStateUpdate: (cb: TItemStateListener) => {
      const itemId = this.hooks.useItemId();
      useEffect(() => {
        if (!this.itemsStateListeners[itemId])
          this.itemsStateListeners[itemId] = [];
        this.itemsStateListeners[itemId].push(cb);

        return () => {
          this.itemsStateListeners[itemId] = this.itemsStateListeners[
            itemId
          ].filter((current) => current != cb);
        };
      }, [cb, itemId]);
    },
    usePropsSelector: makePropsSelectorHook(this),
    usePropsUpdate: (cb: TPropsListener) => {
      useEffect(() => {
        this.propsListeners.push(cb);
        return () => {
          this.propsListeners = this.propsListeners.filter(
            (current) => current !== cb,
          );
        };
      }, [cb]);
    },
  };
}
